/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import Point from '@arcgis/core/geometry/Point';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import isArray from 'lodash.isarray';

/**
 * @useZoomToV2
 *
 * Provides a hook for retrieving an extent.
 *
 * ```typescript
 *
 *    const zoomTo = useZoomToV2();
 *    ...
 *    Get the expanded extent from array of points
 *    const extent = zoomTo?.current(points, 1.2);
 *
 * ```
 */
export const useZoomToV2 = () => {
  const zoomTo = useRef(null);

  useEffect(() => {
    zoomTo.current = function(points: any[], factor: number): __esri.Extent {
      const multipoint = new Multipoint({spatialReference: new SpatialReference({wkid: 4326})});
      points?.forEach(p => {
        const coordinates = isArray(p) ? p : (p?.geometry?.coordinates || p?.coordinates);
        multipoint.addPoint(new Point({longitude: coordinates[0], latitude: coordinates[1]}));
      });
      return multipoint.extent ? multipoint.extent.expand(factor) : null;
    };
  }, []);

  return function(points: any[], factor: number): __esri.Extent {
    return zoomTo?.current ? zoomTo.current(points, factor) : null;
  };
};
  
export default useZoomToV2;
